import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { VehicleProfile, SubletExpense, LaborRecord } from '../types/dossier';
import { VisualizerState } from '../types/vehicle';
import { SubAssembly, HardwareBag, PhotoLog, RestorationTask, RestorationPhase } from '../types/teardown';
import { BOMItem } from '../types/bom';
import {
  DEFAULT_VEHICLE,
  DEFAULT_VISUALIZER,
  DEFAULT_SUB_ASSEMBLIES,
  DEFAULT_HARDWARE_BAGS,
  DEFAULT_BOM_ITEMS,
  DEFAULT_SUBLET_EXPENSES,
  DEFAULT_LABOR_RECORDS,
  DEFAULT_PHOTO_LOGS,
  DEFAULT_TASKS,
} from './defaultData';

export const STORAGE_KEY = 'restomod_studio_state_v1';

export interface RestoModState {
  project: VehicleProfile;
  visualizer: VisualizerState;
  subAssemblies: SubAssembly[];
  hardwareBags: HardwareBag[];
  photoLogs: PhotoLog[];
  tasks: RestorationTask[];
  bomItems: BOMItem[];
  subletExpenses: SubletExpense[];
  laborRecords: LaborRecord[];
  activePhase: RestorationPhase;
}

export type RestoModAction =
  | { type: 'SET_PROJECT'; payload: Partial<VehicleProfile> }
  | { type: 'UPDATE_VISUALIZER'; payload: Partial<VisualizerState> }
  | { type: 'ADD_SUB_ASSEMBLY'; payload: SubAssembly }
  | { type: 'UPDATE_SUB_ASSEMBLY'; payload: SubAssembly }
  | { type: 'DELETE_SUB_ASSEMBLY'; payload: string }
  | { type: 'ADD_HARDWARE_BAG'; payload: HardwareBag }
  | { type: 'UPDATE_HARDWARE_BAG'; payload: HardwareBag }
  | { type: 'DELETE_HARDWARE_BAG'; payload: string }
  | { type: 'ADD_PHOTO_LOG'; payload: PhotoLog }
  | { type: 'UPDATE_PHOTO_LOG'; payload: PhotoLog }
  | { type: 'DELETE_PHOTO_LOG'; payload: string }
  | { type: 'ADD_TASK'; payload: RestorationTask }
  | { type: 'UPDATE_TASK'; payload: RestorationTask }
  | { type: 'DELETE_TASK'; payload: string }
  | { type: 'ADD_BOM_ITEM'; payload: BOMItem }
  | { type: 'UPDATE_BOM_ITEM'; payload: BOMItem }
  | { type: 'DELETE_BOM_ITEM'; payload: string }
  | { type: 'ADD_SUBLET_EXPENSE'; payload: SubletExpense }
  | { type: 'UPDATE_SUBLET_EXPENSE'; payload: SubletExpense }
  | { type: 'DELETE_SUBLET_EXPENSE'; payload: string }
  | { type: 'SET_ACTIVE_PHASE'; payload: RestorationPhase }
  | { type: 'RESET_STATE' }
  | { type: 'LOAD_STATE'; payload: RestoModState | Partial<RestoModState> };

export const INITIAL_STATE: RestoModState = {
  project: DEFAULT_VEHICLE,
  visualizer: DEFAULT_VISUALIZER,
  subAssemblies: DEFAULT_SUB_ASSEMBLIES,
  hardwareBags: DEFAULT_HARDWARE_BAGS,
  photoLogs: DEFAULT_PHOTO_LOGS,
  tasks: DEFAULT_TASKS,
  bomItems: DEFAULT_BOM_ITEMS,
  subletExpenses: DEFAULT_SUBLET_EXPENSES,
  laborRecords: DEFAULT_LABOR_RECORDS,
  activePhase: 'chassis_and_suspension',
};

export const DEFAULT_RESTOMOD_STATE: RestoModState = INITIAL_STATE;

/**
 * Deeply merges and sanitizes partial or untrusted state payloads with DEFAULT_RESTOMOD_STATE.
 * Guarantees all array slices are valid arrays and sub-objects contain default fields,
 * preventing unhandled TypeErrors on .map(), .filter(), or property access.
 */
export function sanitizeRestoModState(incoming: Partial<RestoModState> | null | undefined): RestoModState {
  if (!incoming || typeof incoming !== 'object') {
    return { ...DEFAULT_RESTOMOD_STATE };
  }

  return {
    project: incoming.project
      ? { ...DEFAULT_RESTOMOD_STATE.project, ...incoming.project }
      : { ...DEFAULT_RESTOMOD_STATE.project },
    visualizer: incoming.visualizer
      ? {
          ...DEFAULT_RESTOMOD_STATE.visualizer,
          ...incoming.visualizer,
          paint: incoming.visualizer.paint
            ? { ...DEFAULT_RESTOMOD_STATE.visualizer.paint, ...incoming.visualizer.paint }
            : { ...DEFAULT_RESTOMOD_STATE.visualizer.paint },
          wheel: incoming.visualizer.wheel
            ? { ...DEFAULT_RESTOMOD_STATE.visualizer.wheel, ...incoming.visualizer.wheel }
            : { ...DEFAULT_RESTOMOD_STATE.visualizer.wheel },
          stance: incoming.visualizer.stance
            ? { ...DEFAULT_RESTOMOD_STATE.visualizer.stance, ...incoming.visualizer.stance }
            : { ...DEFAULT_RESTOMOD_STATE.visualizer.stance },
        }
      : { ...DEFAULT_RESTOMOD_STATE.visualizer },
    subAssemblies: Array.isArray(incoming.subAssemblies)
      ? incoming.subAssemblies
      : [...DEFAULT_RESTOMOD_STATE.subAssemblies],
    hardwareBags: Array.isArray(incoming.hardwareBags)
      ? incoming.hardwareBags
      : [...DEFAULT_RESTOMOD_STATE.hardwareBags],
    photoLogs: Array.isArray(incoming.photoLogs)
      ? incoming.photoLogs
      : [...DEFAULT_RESTOMOD_STATE.photoLogs],
    tasks: Array.isArray(incoming.tasks)
      ? incoming.tasks
      : [...DEFAULT_RESTOMOD_STATE.tasks],
    bomItems: Array.isArray(incoming.bomItems)
      ? incoming.bomItems
      : [...DEFAULT_RESTOMOD_STATE.bomItems],
    subletExpenses: Array.isArray(incoming.subletExpenses)
      ? incoming.subletExpenses
      : [...DEFAULT_RESTOMOD_STATE.subletExpenses],
    laborRecords: Array.isArray(incoming.laborRecords)
      ? incoming.laborRecords
      : [...DEFAULT_RESTOMOD_STATE.laborRecords],
    activePhase: incoming.activePhase || DEFAULT_RESTOMOD_STATE.activePhase,
  };
}

function restoModReducer(state: RestoModState, action: RestoModAction): RestoModState {
  switch (action.type) {
    case 'SET_PROJECT':
      return {
        ...state,
        project: {
          ...state.project,
          ...action.payload,
          updatedAt: new Date().toISOString(),
        },
      };

    case 'UPDATE_VISUALIZER':
      return {
        ...state,
        visualizer: {
          ...state.visualizer,
          ...action.payload,
          updatedAt: new Date().toISOString(),
        },
      };

    case 'ADD_SUB_ASSEMBLY':
      return {
        ...state,
        subAssemblies: [action.payload, ...state.subAssemblies],
      };

    case 'UPDATE_SUB_ASSEMBLY':
      return {
        ...state,
        subAssemblies: state.subAssemblies.map((item) =>
          item.id === action.payload.id ? action.payload : item
        ),
      };

    case 'DELETE_SUB_ASSEMBLY':
      return {
        ...state,
        subAssemblies: state.subAssemblies.filter((item) => item.id !== action.payload),
        hardwareBags: state.hardwareBags.filter((b) => b.subAssemblyId !== action.payload),
      };

    case 'ADD_HARDWARE_BAG':
      return {
        ...state,
        hardwareBags: [action.payload, ...state.hardwareBags],
      };

    case 'UPDATE_HARDWARE_BAG':
      return {
        ...state,
        hardwareBags: state.hardwareBags.map((bag) =>
          bag.id === action.payload.id ? action.payload : bag
        ),
      };

    case 'DELETE_HARDWARE_BAG':
      return {
        ...state,
        hardwareBags: state.hardwareBags.filter((bag) => bag.id !== action.payload),
      };

    case 'ADD_PHOTO_LOG':
      return {
        ...state,
        photoLogs: [action.payload, ...state.photoLogs],
      };

    case 'UPDATE_PHOTO_LOG':
      return {
        ...state,
        photoLogs: state.photoLogs.map((p) =>
          p.id === action.payload.id ? action.payload : p
        ),
      };

    case 'DELETE_PHOTO_LOG':
      return {
        ...state,
        photoLogs: state.photoLogs.filter((p) => p.id !== action.payload),
      };

    case 'ADD_TASK':
      return {
        ...state,
        tasks: [action.payload, ...state.tasks],
      };

    case 'UPDATE_TASK':
      return {
        ...state,
        tasks: state.tasks.map((t) =>
          t.id === action.payload.id ? action.payload : t
        ),
      };

    case 'DELETE_TASK':
      return {
        ...state,
        tasks: state.tasks.filter((t) => t.id !== action.payload),
      };

    case 'ADD_BOM_ITEM':
      return {
        ...state,
        bomItems: [action.payload, ...state.bomItems],
      };

    case 'UPDATE_BOM_ITEM':
      return {
        ...state,
        bomItems: state.bomItems.map((item) =>
          item.id === action.payload.id ? action.payload : item
        ),
      };

    case 'DELETE_BOM_ITEM':
      return {
        ...state,
        bomItems: state.bomItems.filter((item) => item.id !== action.payload),
      };

    case 'ADD_SUBLET_EXPENSE':
      return {
        ...state,
        subletExpenses: [action.payload, ...state.subletExpenses],
      };

    case 'UPDATE_SUBLET_EXPENSE':
      return {
        ...state,
        subletExpenses: state.subletExpenses.map((exp) =>
          exp.id === action.payload.id ? action.payload : exp
        ),
      };

    case 'DELETE_SUBLET_EXPENSE':
      return {
        ...state,
        subletExpenses: state.subletExpenses.filter((exp) => exp.id !== action.payload),
      };

    case 'SET_ACTIVE_PHASE':
      return {
        ...state,
        activePhase: action.payload,
      };

    case 'RESET_STATE':
      return INITIAL_STATE;

    case 'LOAD_STATE':
      return sanitizeRestoModState(action.payload);

    default:
      return state;
  }
}

interface RestoModContextType {
  state: RestoModState;
  dispatch: React.Dispatch<RestoModAction>;
  resetToDefault: () => void;
  exportState: () => string;
  importState: (json: string) => boolean;
}

const RestoModContext = createContext<RestoModContextType | undefined>(undefined);

function loadSavedState(): RestoModState {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') {
    return DEFAULT_RESTOMOD_STATE;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_RESTOMOD_STATE;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return sanitizeRestoModState(parsed);
    }
  } catch (err) {
    console.warn('Failed to load saved state from localStorage, falling back to default:', err);
  }
  return DEFAULT_RESTOMOD_STATE;
}

export const RestoModProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(restoModReducer, null, loadSavedState);

  // Sync to localStorage on change
  useEffect(() => {
    try {
      if (typeof window !== 'undefined' && typeof window.localStorage !== 'undefined') {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      }
    } catch (err) {
      console.warn('Failed to persist state to localStorage:', err);
    }
  }, [state]);

  const resetToDefault = () => {
    dispatch({ type: 'RESET_STATE' });
  };

  const exportState = (): string => {
    return JSON.stringify(state, null, 2);
  };

  const importState = (json: string): boolean => {
    try {
      const parsed = JSON.parse(json);
      if (parsed && typeof parsed === 'object') {
        const sanitized = sanitizeRestoModState(parsed);
        dispatch({ type: 'LOAD_STATE', payload: sanitized });
        return true;
      }
    } catch (err) {
      console.error('Invalid RestoMod state JSON:', err);
    }
    return false;
  };

  return (
    <RestoModContext.Provider
      value={{
        state,
        dispatch,
        resetToDefault,
        exportState,
        importState,
      }}
    >
      {children}
    </RestoModContext.Provider>
  );
};

export function useRestoMod(): RestoModContextType {
  const context = useContext(RestoModContext);
  if (!context) {
    throw new Error('useRestoMod must be used within a RestoModProvider');
  }
  return context;
}
